import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { connect } from 'react-redux';
import { getToolIds } from '../../../../../util/tool';

import CustomMuiDropdown from '../../../../../../components/CustomMuiDropdown/CustomMuiDropdown';
import MLSelect from '../../../../../../components/MLSelect/MLSelect';
import MLDialog from '../../../../../../components/MLDialog/MLDialog';
import MLButton from '../../../../../../components/MLButton';
import MLLabel from '../../../../../../components/MLLabel';
import MLText from '../../../../../../components/MLTextField';
import {
  WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
  WIDGET_TYPE_TIME_SCORE_CHART,
  WIDGET_TYPE_TIME_ATTEMPTS_CHART,
  WIDGET_TYPE_COMPLETED_ASSIGNMENT,
  TIME_SCORE_HELPER_TEXT,
  TIME_ATTEMPTS_HELPER_TEXT,
  isSRLWidget,
} from '../../../../constants';
import s from './EditDialog.module.css';

const RANGE_TYPE_DAYS = 'DAYS';
const RANGE_TYPE_ALL_TIME = 'ALL_TIME';

const LAST_7_CODE = 'LAST_7';
const LAST_30_CODE = 'LAST_30';
const ALL_TIME_CODE = 'ALL_TIME';

const dateRanges = [
  { id: LAST_7_CODE, name: 'Last 7 days' },
  { id: LAST_30_CODE, name: 'Last 30 days' },
  { id: ALL_TIME_CODE, name: 'All time' },
];

const getDefaultDateRangeSelectOption = (currentWidgetData) => {
  let defaultDateRangeCode;
  if (currentWidgetData.rangeType === RANGE_TYPE_DAYS) {
    if (currentWidgetData.minRangeDays === 7 && currentWidgetData.maxRangeDays === 0) {
      defaultDateRangeCode = LAST_7_CODE;
    } else if (currentWidgetData.minRangeDays === 30 && currentWidgetData.maxRangeDays === 0) {
      defaultDateRangeCode = LAST_30_CODE;
    }
  } else if (currentWidgetData.rangeType === RANGE_TYPE_ALL_TIME) {
    defaultDateRangeCode = ALL_TIME_CODE;
  }
  return defaultDateRangeCode;
};

const EditDialog = ({
  handleClose,
  onSave,
  currentWidgetData,
  saving,
  assignments,
  timeOnTaskAssignments,
}) => {
  const [data, setData] = useState(currentWidgetData);
  const defaultDateRangeSelectOption = getDefaultDateRangeSelectOption(currentWidgetData);
  const [dateRangeCode, setDateRangeCode] = useState(defaultDateRangeSelectOption);
  const [selectedAssignmentIndex, setSelectedAssignmentIndex] = useState(null);
  const [errors, setErrors] = useState([]);
  const isAssignmentSnapshotWidget = currentWidgetData.widgetType === WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART;
  const isTimeScoreWidget = currentWidgetData.widgetType === WIDGET_TYPE_TIME_SCORE_CHART;
  const isTimeAttemptsWidget = currentWidgetData.widgetType === WIDGET_TYPE_TIME_ATTEMPTS_CHART;
  const isCompletedAssignment = currentWidgetData.widgetType === WIDGET_TYPE_COMPLETED_ASSIGNMENT;
  const isTimeOnTaskWidget = isTimeScoreWidget || isTimeAttemptsWidget;
  const assignmentsToShow = isTimeOnTaskWidget ? timeOnTaskAssignments : assignments;

  const updateAssignmentIndex = (value) => {
    const index = assignmentsToShow.findIndex(a => a.value === value);
    setSelectedAssignmentIndex(index);
  };

  useEffect(() => {
    const assignmentIds = get(currentWidgetData, 'assignmentIds[0]', null);
    const getSelectedAssignmentIndex = () => {
      if (assignmentIds && (isAssignmentSnapshotWidget || isTimeOnTaskWidget || isCompletedAssignment)) {
        updateAssignmentIndex(assignmentIds);
      }
    };
    getSelectedAssignmentIndex();
  }, [currentWidgetData, assignments]);

  const handleTitleChange = (e) => {
    const { target: { value } } = e;
    setData({ ...data, title: value });
    setErrors([]);
  };

  const handleDateRangeChange = ({ id }) => {
    setDateRangeCode(id);
    switch (id) {
      case LAST_7_CODE:
        setData({
          ...data,
          rangeType: RANGE_TYPE_DAYS,
          minRangeDays: 7,
          maxRangeDays: 0,
        });
        break;
      case LAST_30_CODE:
        setData({
          ...data,
          rangeType: RANGE_TYPE_DAYS,
          minRangeDays: 30,
          maxRangeDays: 0,
        });
        break;
      case ALL_TIME_CODE:
        setData({
          ...data,
          rangeType: RANGE_TYPE_ALL_TIME,
          minRangeDays: null,
          maxRangeDays: null,
        });
        break;
      default:
        break;
    }
  };

  const onChangeAssignments = (value) => {
    setData({
      ...data,
      assignmentIds: [value],
    });
    updateAssignmentIndex(value);
  };

  const save = () => {
    if (data.title.trim() === '') {
      setErrors(['Required Field']);
      return;
    }
    onSave(data);
    setErrors([]);
  };

  const showRangeData =
    !isCompletedAssignment &&
    !isAssignmentSnapshotWidget &&
    !isTimeOnTaskWidget &&
    !isSRLWidget(currentWidgetData.widgetType);

  return (
    <MLDialog
      show
      title="Configure Insight"
      onClose={handleClose}
      actions={[
        <MLButton
          btnType="secondary"
          btnText="Cancel"
          onClick={handleClose}
          disabled={saving}
          testId="insight-card-config-cancel"
        />,
        <MLButton
          btnType="primary"
          btnText="Save"
          disabled={saving}
          onClick={save}
          testId="insight-card-config-save"
        />]}
    >
      <Fragment>
        <MLLabel label="Insight Title" className={s.label} />
        <MLText
          onChange={handleTitleChange}
          value={data.title}
          className={s.inputField}
          containerClassName={s.inputContainer}
          errorMessages={errors}
          hasError={!!errors.length}
        />
      </Fragment>
      {showRangeData &&
        (<Fragment>
          <MLLabel label="Date Range" className={s.label} />
          <div className={s.dropdownView}>
            <CustomMuiDropdown
              className={s.selectRange}
              items={dateRanges}
              labelProp={'name'}
              valueProp={'id'}
              defaultValue={dateRangeCode}
              onClickItem={handleDateRangeChange}
            />
          </div>
        </Fragment>)
      }
      {!showRangeData &&
        (<Fragment>
          <MLLabel label="Select Assignment" className={s.label} />
          {isTimeScoreWidget && <span className={s.helperText}>{TIME_SCORE_HELPER_TEXT}</span>}
          {isTimeAttemptsWidget && <span className={s.helperText}>{TIME_ATTEMPTS_HELPER_TEXT}</span>}
          <div className={s.dropdownView}>
            <MLSelect
              className={s.selectAssignment}
              options={assignmentsToShow}
              placeHolder="Select an assignment"
              onChange={onChangeAssignments}
              defaultOptionIndex={selectedAssignmentIndex}
            />
          </div>
        </Fragment>)
      }
    </MLDialog>
  );
};
EditDialog.propTypes = {
  handleClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  currentWidgetData: PropTypes.object,
  saving: PropTypes.boolean,
  assignments: PropTypes.array,
  timeOnTaskAssignments: PropTypes.array,
};


function mapStateToProps(state) {
  const assignments = get(state, 'insights.dashboard.assignments', []);
  const timeOnTaskAssignments = [];
  const { learningcurve: learningcurveToolId, assessment: assessmentToolId } = getToolIds();
  assignments.forEach((a) => {
    if ([assessmentToolId, learningcurveToolId].includes(a.tool)) timeOnTaskAssignments.push(a);
  });
  return { assignments, timeOnTaskAssignments };
}
const WrappedEditDialog = connect(mapStateToProps)(EditDialog);

export default WrappedEditDialog;
